import React, {
  useContext, useEffect, useMemo, useState,
} from "react";

import { Layout } from "antd";
import Lottie from "lottie-react";
import { Route, Switch, Redirect } from "react-router-dom";

import { AppContext } from "../AppContext";
import AddCompanyLandingPage from "./AddCompany/AddCompanyLandingPage";
import AuthFailedPage from "./AuthFailedPage";
import Documents from "./Documents/Documents";
import Env from "./Env/Env";
import { AccessStatusProvider } from "./grace-period/hooks/useAccessStatus";
import { GraceInvoicePaymentProvider } from "./grace-period/hooks/useGraceInvoicePayment";
import AccountFrozenModal from "./grace-period/modals/AccountFrozenModal";
import Index from "./Index/Index";
import NavBar from "./Layout/NavBar";
import Invoice from "./MyAccount/Invoice/Invoice";
import MyAccountHelp from "./MyAccount/MyAccountHelp";
import MyProfile from "./MyAccount/MyProfile/MyProfile";
import TeamMember from "./MyAccount/TeamMember/TeamMember";
import UsageMetrics from "./MyAccount/UsageMetrics/UsageMetricsPage";
import Product from "./Product/Product";
import ProtectedRoute from "./ProtectedRoute";
// Disabled: QMS requires @reduxjs/toolkit, react-redux, @tiptap/*, @dnd-kit/*, react-hook-form, and zod, which are not in the current package.json.
// import QMS from "./Qms/Qms";
import EnvironmentLandingPage from "./SampleSubmission/Onboarding/EnvironmentOnboarding/EnvironmentLandingPage";
import ProductLandingPage from "./SampleSubmission/Onboarding/ProductOnboarding/ProductLandingPage";
import SampleSubmissionNav from "./SampleSubmission/SampleSubmissionNav";
import SupplyPickup from "./SupplyPickup/SupplyPickup";
import UnderMaintenance from "./UnderMaintenance";
// Disabled: Workflow is QMS-related and is kept off with QMS until those package.json deps are restored.
// import Workflow from "./Workflow/Workflow";

import {
  getCustomerConfig,
  isAddCompanyBlocked,
  isUser,
  transformAccountPermissions,
  updateEsvDetails,
} from "../utils/common";
import {
  permissions as permissionCodes, hasAnyTabPermission, getDefaultRoute, hasPermission,
} from "../utils/Permissions";
import { canViewInvoice, canViewUsageMetrics } from "./MyAccount/UsageMetrics/utils";

import { getAccountPermissionForCompany } from "../actions/switchCompany";

import { ReactComponent as Logo } from "../assets/icons/genista-logo-navbar.svg";
import loadingAnimation from "../assets/images/common/animation/DashboardLoadingAnimation.json";
import loadingBlob from "../assets/images/common/animation/LoadingBlob.svg";

// import ChangeLog from "./Common/ChangeLog/ChangeLog";

const { Content } = Layout;

const LoggedIn = (routeProps) => {
  const { location, history } = routeProps;
  const [reports_link, setReports] = useState("");
  const [product_link, setProductLink] = useState("");
  const [sample_sub_link, setSampleSubLink] = useState("");
  const [loading, setLoading] = useState(true);
  const [accountPermissionsEmpty, setAccountPermissionsEmpty] = useState(false);
  const { userTitle, initialRoleCheckDone } = useContext(AppContext);

  const optionalTabRoutes = useMemo(() => [
    {
      tab: "index",
      path: "/index",
      exact: true,
      component: () => (<Index onChangeTab={setProductLink} />),
      requiredPermission: permissionCodes.tabs.index,
    },
    {
      tab: "product",
      path: "/product",
      exact: false,
      component: (props) => (<Product history={props.history} onChangeReport={setReports} />),
      requiredPermission: permissionCodes.tabs.product,
    },
    {
      tab: "environment",
      path: "/environment",
      exact: false,
      component: (props) => (<Env history={props.history} onChangeReport={setReports} />),
      requiredPermission: permissionCodes.tabs.environment,
    },
    // Disabled: Swabathon feature (missing package.json dep: @dnd-kit/core)
    // {
    //   tab: "environment",
    //   path: "/environment/swabathon",
    //   exact: true,
    //   component: (props) => (<Env history={props.history} onChangeReport={setReports} />),
    //   requiredPermission: permissionCodes.tabs.environment,
    // },
    {
      tab: "supply-pickup",
      path: "/supply-pickup",
      exact: false,
      component: (props) => (<SupplyPickup history={props.history} />),
      requiredPermission: permissionCodes.tabs.schedules,
    },
    {
      tab: "documents",
      path: "/documents",
      exact: true,
      component: Documents,
      requiredPermission: permissionCodes.tabs.documents,
    },
    {
      tab: "sample-submission",
      path: "/sample-submission",
      exact: false,
      component: (props) => (
        <SampleSubmissionNav
          history={props.history}
          location={props.location}
          setSampleSubLink={setSampleSubLink}
        />
      ),
      requiredPermission: permissionCodes.tabs.sampleSubmission,
    },
    {
      tab: "sample-submission",
      path: "/onboarding/product",
      exact: true,
      component: () => (<ProductLandingPage />),
      requiredPermission: null,
    },
    {
      tab: "sample-submission",
      path: "/onboarding/environment",
      exact: true,
      component: () => (<EnvironmentLandingPage />),
      requiredPermission: null,
    },
    // Disabled: QMS feature (missing package.json deps: @reduxjs/toolkit, react-redux, @tiptap, @dnd-kit, react-hook-form, zod)
    // {
    //   tab: "qms",
    //   path: "/qms/dms",
    //   exact: true,
    //   component: (props) => (<QMS history={props.history} onChangeReport={setReports} />),
    //   requiredPermission: permissionCodes.tabs.qms,
    // },
    // {
    //   tab: "qms",
    //   path: "/qms/ncr",
    //   exact: true,
    //   component: (props) => (<QMS history={props.history} onChangeReport={setReports} />),
    //   requiredPermission: permissionCodes.tabs.qms,
    // },
    // {
    //   tab: "qms",
    //   path: "/qms/moc",
    //   exact: true,
    //   component: (props) => (<QMS history={props.history} onChangeReport={setReports} />),
    //   requiredPermission: permissionCodes.tabs.qms,
    // },
    // Disabled: Workflow (QMS-related) feature (missing package.json deps)
    // {
    //   tab: "qms-workflow",
    //   path: "/workflow",
    //   exact: true,
    //   component: Workflow,
    //   requiredPermission: permissionCodes.tabs.workflow,
    // },
  ], []); // eslint-disable-line react-hooks/exhaustive-deps
  const [routes, setRoutes] = useState(optionalTabRoutes);
  const FALLBACK_ROUTES = ["/maintenance", "/auth-failed"];

  /** Fetch tabs only after role check has run */
  useEffect(() => {
    // Wait for the role check before deciding anything.
    if (!initialRoleCheckDone) return;

    if (!userTitle || !isUser() || FALLBACK_ROUTES.includes(location.pathname)) {
      setLoading(false);
      return;
    }

    async function apiGetTabs() {
      setLoading(true);
      try {
        const {
          accountPermissions, customerConfig,
        } = await getAccountPermissionForCompany();

        // Transform account_permissions from API to flattened format
        const flattenedAccountPermissions = (accountPermissions && Array.isArray(accountPermissions))
          ? transformAccountPermissions(accountPermissions)
          : {};

        // Helper to check permissions using the flattened API permissions
        const checkPermission = (code) => flattenedAccountPermissions[code] === true;

        updateEsvDetails({
          accountPermissions: flattenedAccountPermissions,
          customer_config: customerConfig,
        });

        // Leave billing pages when the account or role cannot open them
        if (
          (location.pathname === "/myaccount/usage-metrics" && !canViewUsageMetrics())
          || (location.pathname === "/myaccount/invoice" && !canViewInvoice())
        ) {
          history.replace("/sample-submission");
        }

        // Check if user has any tab permissions (view_xyz permissions)
        // If no tab permissions exist, show UnderMaintenance page
        const hasTabs = hasAnyTabPermission();
        setAccountPermissionsEmpty(!hasTabs);

        // Determine which routes to show based on onboarding logic
        // (Hiding onboarding routes if user already has access to product/environment)
        let routesToUse = [...optionalTabRoutes];

        // Adjust routes if "product" tab permission is enabled - hide onboarding route
        if (checkPermission(permissionCodes.tabs.product)) {
          routesToUse = routesToUse.filter((item) => item.path !== "/onboarding/product");
        }

        // Adjust routes if "environment" tab permission is enabled - hide onboarding route
        if (checkPermission(permissionCodes.tabs.environment)) {
          routesToUse = routesToUse.filter((item) => item.path !== "/onboarding/environment");
        }

        setRoutes(routesToUse);
      } catch (error) {
        console.error("Error fetching tabs:", error); // eslint-disable-line no-console
        // Fallback to default routes on error
        setRoutes(optionalTabRoutes);
        // Check if user has any tab permissions even on error
        const hasTabs = hasAnyTabPermission();
        setAccountPermissionsEmpty(!hasTabs);
      } finally {
        setLoading(false);
      }
    }
    apiGetTabs();
  }, [userTitle, initialRoleCheckDone]); //eslint-disable-line

  if (!isUser()) {
    return <Redirect to="/login" />;
  }

  // Wait for role check so we fetch tabs once with the correct role
  if (!initialRoleCheckDone || loading) {
    return (
      <div className="LoggedIn__Loading">
        <div className="LoggedIn__Loading__Art" aria-hidden="true">
          <img className="LoggedIn__Loading__Blob" src={loadingBlob} alt="" />
          <span className="LoggedIn__Loading__BlobDot LoggedIn__Loading__BlobDot--left" />
          <span className="LoggedIn__Loading__BlobDot LoggedIn__Loading__BlobDot--right" />
          <Lottie
            animationData={loadingAnimation}
            loop
            className="LoggedIn__Loading__Animation"
          />
        </div>
        <div className="LoggedIn__Loading__Text">
          Loading
          <Logo className="LoggedIn__Loading__Icon" />
        </div>
      </div>
    );
  }

  // If user has no tab permissions, show UnderMaintenance page
  if (accountPermissionsEmpty) {
    return <UnderMaintenance history={history} />;
  }

  const layoutStyle = { background: "var(--color-bg-primary)" };

  return (
    <AccessStatusProvider>
      <GraceInvoicePaymentProvider>
        <AccountFrozenModal />
        <Layout>
          {(!FALLBACK_ROUTES.includes(location.pathname)
          && (
            <NavBar
              history={history}
              // analytics={analytics}
              reports={reports_link}
              product_link={product_link}
              sample_sub_link={sample_sub_link}
            />
          ))}
          <Layout
            className={`${FALLBACK_ROUTES.includes(location.pathname) ? "" : "site-layout"} sm-margin-0`}
            style={layoutStyle}
          >
            <Content
              style={FALLBACK_ROUTES.includes(location.pathname) ? {} : { marginTop: "60px", background: "var(--color-bg-primary)" }}
            >
              <Switch>
                {/* All routes use ProtectedRoute - handles permission checks and redirects to /sample-submission */}
                {routes.map(({
                  path, component, exact, requiredPermission,
                }) => (
                  <ProtectedRoute
                    key={path}
                    path={path}
                    exact={exact}
                    component={component}
                    requiredPermission={requiredPermission}
                  />
                ))}
                {/* Catch-all protected routes for direct URL access to paths that might not be in routes array */}
                {/* Onboarding routes - should always be accessible (requiredPermission: null) */}
                <ProtectedRoute
                  path="/onboarding/product"
                  exact
                  component={() => (<ProductLandingPage />)}
                  requiredPermission={null}
                />
                <ProtectedRoute
                  path="/onboarding/environment"
                  exact
                  component={() => (<EnvironmentLandingPage />)}
                  requiredPermission={null}
                />
                <ProtectedRoute
                  path="/product"
                  exact={false}
                  component={(props) => (<Product history={props.history} onChangeReport={setReports} />)}
                  requiredPermission={permissionCodes.tabs.product}
                />
                <ProtectedRoute
                  path="/environment"
                  exact={false}
                  component={(props) => (<Env history={props.history} onChangeReport={setReports} />)}
                  requiredPermission={permissionCodes.tabs.environment}
                />
                <ProtectedRoute
                  path="/index"
                  exact
                  component={() => (<Index onChangeTab={setProductLink} />)}
                  requiredPermission={permissionCodes.tabs.index}
                />
                <ProtectedRoute
                  path="/documents"
                  exact
                  component={Documents}
                  requiredPermission={permissionCodes.tabs.documents}
                />
                <ProtectedRoute
                  path="/supply-pickup"
                  exact={false}
                  component={(props) => (<SupplyPickup history={props.history} />)}
                  requiredPermission={permissionCodes.tabs.schedulesPickups}
                />
                {/* Disabled: QMS feature (missing package.json deps)
            <ProtectedRoute
              path="/qms"
              exact={false}
              component={(props) => (<QMS history={props.history} onChangeReport={setReports} />)}
              requiredPermission={permissionCodes.tabs.qms}
            />
            */}
                {/* Disabled: Workflow (QMS-related) feature (missing package.json deps)
            <ProtectedRoute
              path="/workflow"
              exact
              component={Workflow}
              requiredPermission={permissionCodes.tabs.workflow}
            />
            */}
                <Route path="/myaccount/profile" exact component={MyProfile} />
                <Route
                  path="/myaccount/usage-metrics"
                  exact
                  render={(props) => (
                    canViewUsageMetrics()
                      ? (
                        <UsageMetrics
                          history={props.history}
                          location={props.location}
                          match={props.match}
                        />
                      )
                      : <Redirect to={getDefaultRoute()} />
                  )}
                />
                <Route
                  path="/myaccount/invoice"
                  exact
                  render={(props) => (
                    canViewInvoice()
                      ? <Invoice history={props.history} location={props.location} match={props.match} />
                      : <Redirect to={getDefaultRoute()} />
                  )}
                />
                <Route path="/myaccount/members" exact component={TeamMember} />
                <Route path="/myaccount/help" exact component={MyAccountHelp} />
                <Route
                  path="/add-company"
                  exact
                  render={(props) => (
                    hasPermission(permissionCodes.onboarding.addCompany)
                    && !isAddCompanyBlocked(getCustomerConfig()?.customer_type)
                      ? <AddCompanyLandingPage history={props.history} />
                      : <Redirect to={getDefaultRoute()} />
                  )}
                />
                <Route path="/maintenance" component={(props) => (<UnderMaintenance history={props.history} />)} />
                <Route path="/auth-failed" component={(props) => (<AuthFailedPage history={props.history} />)} />
                <Route path="/" render={() => <Redirect to={getDefaultRoute()} />} />
              </Switch>
            </Content>
          </Layout>
        </Layout>
      </GraceInvoicePaymentProvider>
    </AccessStatusProvider>
  );
};

export default LoggedIn;
